// src/components/SlugSelectionModal.js
// First-publish URL chooser modal.
// Shown when a practitioner publishes for the first time and has no slug.
// Auto-suggests a slug from their display name, validates format, checks Firestore availability.

import React, { useState, useEffect, useRef } from 'react';
import { Globe, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { validateSlug, generateSlugSuggestion, isSlugAvailable } from '../utils/slug-utils';

const BASE_URL = 'divineframeworks.com/site/';

/**
 * Props:
 *   isOpen: boolean
 *   practitionerName: string — used to auto-suggest a slug
 *   onConfirm: (slug: string) => void — called with the confirmed slug
 *   onCancel: () => void
 *   mode: 'claim' | 'change' — 'claim' is the default first-publish flow;
 *         'change' is used when the practitioner wants to update an existing URL
 *   currentSlug: string — pre-fills the input when mode === 'change'
 */
const SlugSelectionModal = ({ isOpen, practitionerName, onConfirm, onCancel, mode = 'claim', currentSlug }) => {
  const [slug, setSlug] = useState('');
  const [validation, setValidation] = useState({ valid: null, error: null });
  const [availability, setAvailability] = useState(null); // null | 'checking' | 'available' | 'taken' | 'error'
  const [confirming, setConfirming] = useState(false);
  const debounceTimer = useRef(null);
  const inputRef = useRef(null);

  // Auto-suggest slug from practitioner name on open (claim mode),
  // or pre-fill with current slug (change mode).
  useEffect(() => {
    if (!isOpen) return;
    const initial =
      mode === 'change' && currentSlug
        ? currentSlug
        : generateSlugSuggestion(practitionerName);
    if (initial) {
      setSlug(initial);
      const result = validateSlug(initial);
      setValidation(result);
      if (result.valid) {
        checkAvailability(initial);
      }
    }
  }, [isOpen, practitionerName, mode, currentSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const checkAvailability = (value, retryCount = 0) => {
    const { valid } = validateSlug(value);
    if (!valid) {
      setAvailability(null);
      return;
    }
    setAvailability('checking');
    isSlugAvailable(value)
      .then((available) => setAvailability(available ? 'available' : 'taken'))
      .catch(() => {
        if (retryCount < 1) {
          // Retry once after 1 second before showing error
          setTimeout(() => checkAvailability(value, retryCount + 1), 1000);
        } else {
          setAvailability('error');
        }
      });
  };

  const handleChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlug(value);

    // Validate synchronously
    const result = validateSlug(value);
    setValidation(result);

    if (!result.valid) {
      setAvailability(null);
      return;
    }

    // Debounce availability check
    setAvailability('checking');
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      checkAvailability(value);
    }, 500);
  };

  const handleConfirm = async () => {
    if (!slug || !validation.valid || availability !== 'available' || confirming) return;
    setConfirming(true);
    onConfirm(slug);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleConfirm();
    if (e.key === 'Escape') onCancel();
  };

  const isReady = validation.valid && availability === 'available' && !confirming;

  const getAvailabilityUI = () => {
    if (!slug || !validation.valid) return null;
    if (availability === 'checking') {
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6B7280', fontSize: '13px' }}>
          <Loader2 size={13} className="animate-spin" />
          Checking...
        </span>
      );
    }
    if (availability === 'available') {
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#16a34a', fontSize: '13px', fontWeight: 500 }}>
          <CheckCircle size={13} />
          ✓ Available!
        </span>
      );
    }
    if (availability === 'taken') {
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#dc2626', fontSize: '13px' }}>
          <XCircle size={13} />
          <strong>{slug}</strong>&nbsp;is already taken. Try a different name.
        </span>
      );
    }
    if (availability === 'error') {
      return (
        <span style={{ fontSize: '13px', color: '#6B7280' }}>
          Checking availability… Please try again in a moment.
        </span>
      );
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
        {/* Header */}
        <div className="flex items-start gap-3 mb-5">
          <div className="p-2 bg-purple-50 rounded-lg flex-shrink-0">
            <Globe className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {mode === 'change' ? 'Change your website address' : 'Choose your website address'}
            </h3>
            <p className="text-sm text-gray-500">
              {mode === 'change'
                ? 'Pick a new URL handle. Your old link will stop working once changed.'
                : 'This is the public URL where clients can find your website.'}
            </p>
          </div>
        </div>

        {/* URL Preview */}
        <div
          className="rounded-lg px-4 py-3 mb-4 flex items-center gap-2"
          style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}
        >
          <Globe size={14} className="text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-500 flex-shrink-0">{BASE_URL}</span>
          <span
            className="text-sm font-semibold"
            style={{ color: slug ? '#7c3aed' : '#9CA3AF' }}
          >
            {slug || 'your-name'}
          </span>
        </div>

        {/* Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            URL handle
          </label>
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={slug}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="your-name"
              maxLength={30}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              style={{
                borderColor: validation.valid === false ? '#dc2626' : availability === 'available' ? '#16a34a' : '#D1D5DB',
              }}
            />
          </div>

          {/* Validation error OR availability status */}
          <div className="mt-1.5 h-5">
            {validation.valid === false ? (
              <p className="text-xs text-red-600">{validation.error}</p>
            ) : (
              getAvailabilityUI()
            )}
          </div>

          {/* Helper text */}
          <p className="text-xs text-gray-400 mt-1">
            3–30 characters. Lowercase letters, numbers, and hyphens only.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={confirming}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!isReady}
            className="px-5 py-2 text-sm font-semibold text-white rounded-lg transition-all"
            style={{
              backgroundColor: isReady ? '#7c3aed' : '#D1D5DB',
              cursor: isReady ? 'pointer' : 'not-allowed',
            }}
          >
            {confirming ? 'Saving...' : mode === 'change' ? 'Save New URL' : 'Confirm & Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SlugSelectionModal;
